import {
    cancelFlow_offerResultClick,
    cancelFlow_offerResultScreenLoad,
    OfferTypes,
} from 'analytics/trackers/cancelFlow';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ModalName } from 'store/modals/types';
import { resumeSubscription } from 'store/subscriptions/actions';

import { DATE_FORMAT } from 'constants/date';

import { useDispatch } from 'hooks';

import { getFormattedDate } from 'helpers/date';

import { ISubscription } from 'types/subscription';

import { useModals } from '../modals';

const useResumeSubscription = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { openModal, closeModal } = useModals();

    const productRef = useRef<ISubscription | null>(null);

    const handleFailedResume = () => {
        openModal(ModalName.universalModal, {
            image: 'errorAttention',
            onClose: () => {
                cancelFlow_offerResultClick({
                    action: 'close',
                    result: 'fail',
                    offer_type: OfferTypes.Pause_offer,
                });
                closeModal();
            },
            onPrimaryClick: () => {
                cancelFlow_offerResultClick({
                    action: 'try_again',
                    result: 'fail',
                    offer_type: OfferTypes.Pause_offer,
                });
                closeModal();
                initResumeSubscription(productRef.current as ISubscription);
            },
            onContactUsClick: () =>
                cancelFlow_offerResultClick({
                    action: 'contact_support',
                    result: 'fail',
                    offer_type: OfferTypes.Pause_offer,
                }),
            withCloseButton: true,
            withContactUsLink: true,
            captions: {
                title: t('subscription.updating.issueModal.title'),
                primaryButton: t('basics.tryAgain'),
                tertiaryButton: t('basics.contactSupport'),
            },
            dataLocators: {
                modal: 'resumeSubModal',
                container: 'resumeSubscriptionPlanModal',
                primaryButton: 'errorModalTryAgain',
                closeButton: 'errorModalCloseButton',
            },
        });
    };

    const handleSuccessfulResume = (sub: ISubscription) => {
        openModal(ModalName.universalModal, {
            image: 'thumbUp',
            onClose: () => {
                cancelFlow_offerResultClick({
                    action: 'close',
                    result: 'success',
                    offer_type: OfferTypes.Pause_offer,
                    subscription_id: sub.external_id,
                });
                closeModal();
            },
            onPrimaryClick: () => {
                cancelFlow_offerResultClick({
                    action: 'got_it',
                    result: 'success',
                    offer_type: OfferTypes.Pause_offer,
                    subscription_id: sub.external_id,
                });
                closeModal();
            },
            withCloseButton: true,
            captions: {
                title: t('subscription.resume.success.title'),
                subtitle: t('subscription.cancelFlow.pauseOffer.successModal.subtitle', {
                    renewalDate: getFormattedDate(sub.expired_date, DATE_FORMAT),
                }),
                primaryButton: t('basics.gotIt'),
            },
        });
    };

    const initResumeSubscription = (candidate: ISubscription) => {
        productRef.current = candidate;

        openModal(ModalName.waitingModal, {
            captions: {
                title: t('subscription.updatingPlan.inProgress'),
            },
        });
        dispatch(
            resumeSubscription({
                id: candidate.external_id,
                provider: candidate.payment_provider,
                onSuccess: (subs) => {
                    const data = subs.find((s) => s.external_id === candidate.external_id);

                    cancelFlow_offerResultScreenLoad({
                        result: 'success',
                        offer_type: OfferTypes.Pause_offer,
                        subscription_id: candidate.external_id,
                    });

                    handleSuccessfulResume(data!);
                },
                onError: () => {
                    handleFailedResume();
                },
            })
        );
    };

    return {
        initResumeSubscription,
    };
};

export default useResumeSubscription;
